/**
 * IndicatorCheckbox component for rendering checkboxes for climate indicators
 * Extracted from NestedList.jsx for better reusability and organization
 */

import { Checkbox } from '@mui/material';
import SquareIcon from '@mui/icons-material/Square';
import useNavigationState from '../hooks/ui/useNavigationState';

/**
 * IndicatorCheckbox component for rendering checkboxes for indicators
 * @param {Object} props - Component props
 * @param {string} props.itemKey - Key of the item
 * @param {boolean} props.isFixedIndicator - Whether the indicator is fixed
 * @param {string} props.color - Color for the indicator
 * @param {Function} props.scrollTo - Function to scroll to element and update URL
 * @returns {JSX.Element|null} Checkbox component or null
 */
const IndicatorCheckbox = ({
  itemKey,
  isFixedIndicator,
  color,
  scrollTo,
}) => {
  const { toggleItem, getItemState } = useNavigationState();
  const checked = getItemState(itemKey);

  if (isFixedIndicator) {
    return (
      <Checkbox
        defaultChecked
        disabled
        icon={<SquareIcon />}
        checkedIcon={<SquareIcon />}
        sx={{ color, '&.Mui-checked': { color } }}
        inputProps={{ 'aria-labelledby': `title-${itemKey}` }}
      />
    );
  }

  return (
    <Checkbox
      key={itemKey + checked} // Force re-render when checked changes
      edge="start"
      checked={checked}
      onChange={() => {
        // If unchecked, scroll to the element when checking
        if (!checked) {
          setTimeout(() => {
            scrollTo(itemKey);
          }, 200);
        }
        toggleItem(itemKey);
      }}
      tabIndex={-1}
      sx={{ color, '&.Mui-checked': { color } }}
      inputProps={{ 'aria-labelledby': `title-${itemKey}` }}
    />
  );
};

IndicatorCheckbox.displayName = 'IndicatorCheckbox';

export default IndicatorCheckbox;
